/**
 * MONSTER TOWN — ORDER CARDS AS PICTURE-SPEECH. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * WHAT THIS IS NOW, AND WHY IT CHANGED.
 *
 * It used to be a RAIL: three cards pinned in a row across the top of the
 * screen while the customers who ordered them sat scattered around the floor.
 * There was no leader line, no proximity and no shared shape between a card and
 * a monster — only a colour band — and the monsters themselves said nothing but
 * "...". A three-year-old was being asked to hold "the yellow card belongs to
 * the yellow blob on the far side of the room" in their head, which is exactly
 * the listening-and-remembering game this venue is FOR, made impossible.
 *
 * A card is now a SPEECH BUBBLE. It hangs over the head of the monster that
 * asked, follows it every frame, wears that monster's body colour, and has a
 * tail that touches it. There is nothing left to pair up: the order is drawn on
 * the creature doing the asking.
 *
 * WHAT IS DRAWN COMES FROM DATA, NOT FROM AN ID. Cards used to work the picture
 * out by string-matching the item id (`id.includes('cone')`), so the restaurant
 * — whose menu is `base_plate` and `food_pasta` — drew a plate and a pink
 * ice-cream cone on every ticket in a room that serves dinner. The venue crew
 * now sends the declared shape and colour of every item (`VenuePics`), so a new
 * venue's menu is drawn correctly the day it is written.
 *
 * NO READING. A card is: a picture of each thing, and counting dots. LAW #2: it
 * never expires, never turns red, never counts down. It sits there happily.
 *
 * DECOUPLING: this file imports nothing from `src/venue/**` at runtime. It
 * listens to the venue crew's declaration-merged events, which TypeScript
 * resolves through the merged `GameEvents` map. If the venue crew is not
 * installed, nothing here ever appears — it costs a few idle listeners.
 */

import { bus } from '../core/events'
import { css, type PaletteName } from '../core/palette'
import { app } from '../app/context'
import { el } from './dom'
import { iconEl } from './icons'
import { monsterPortrait } from './monsterArt'
import { DEFAULT_MONSTER, type MonsterConfig } from '../core/types'

/**
 * Declared item shape -> picture. DATA, and the ONLY mapping there is. A new
 * shape is a line here; there is no id-sniffing fallback to be wrong.
 */
const SHAPE_ICON: Record<string, string> = {
  cone: 'cone',
  // `cup` used to fall through to `plate`, a flat oval seen edge-on. Next to a
  // cone it read as "a plain oval" rather than as a thing you put food in.
  cup: 'cup',
  plate: 'plate',
  // `portion`, not `scoop`: `scoop` has a cone baked into it, which drew an
  // ice-cream cone under every portion — including on a restaurant's plate of
  // pasta. The base slot is what says what the food is in.
  ball: 'portion',
  cherry: 'cherry',
  sprinkles: 'sparkle',
  sauce: 'sauce',
}

/** What one drawable thing looks like. Mirrors the venue crew's `VenuePic`. */
export interface TicketPic {
  readonly shape: string
  readonly color: PaletteName
}

/** A whole serving as pictures. Mirrors the venue crew's `VenuePics`. */
export interface TicketPics {
  readonly base: TicketPic | null
  readonly scoops: readonly TicketPic[]
  readonly topping: TicketPic | null
}

function itemPicture(pic: TicketPic, size: number): HTMLElement {
  return iconEl(SHAPE_ICON[pic.shape] ?? 'scoop', size, { f: pic.color })
}

export interface TicketApi {
  /** Screenshot/QA hook: show a plausible set of cards without a venue. */
  demo(): void
  clear(): void
  count(): number
}

interface Ticket {
  key: string
  node: HTMLElement
  /** The tail, tinted to the customer. Hidden while the card is unanchored. */
  tail: HTMLElement
  color: string
  placed: boolean
}

const MAX_TICKETS = 4

/**
 * Card geometry, in CSS px. Duplicated in `ui.css`; the clamp maths needs it.
 *
 * HALF THE SIZE THEY WERE (venue fix, r3). At 168x150 three open orders covered
 * the counter, the kitchen and the middle of the floor — the round-3 critic
 * measured "more than half the play space hidden behind UI", with two of the
 * three cards overlapping each other. A card is a speech bubble ON a monster's
 * head, so it only has to carry the three pictures; the hero portrait it used to
 * wear was 40% of its height saying something the card's own colour and its
 * position already say.
 */
const CARD_W = 116
const CARD_H = 86
/** Resting gap between the bottom of a card and the head it belongs to. */
const TAIL_H = 16
/**
 * How much a card may be nudged to stop it sitting on another one. Cards are
 * pushed apart along the shortest axis; anything that still cannot find room
 * inside this budget stays where it is rather than flying off its monster.
 */
const SEPARATE_PASSES = 3

/**
 * KEEP CARDS OFF THE HUD FURNITURE.
 *
 * The purse, the left rail, the camera cluster, the carry chip and the hammer
 * are fixed-pixel chrome (88px targets plus padding), so these are fixed pixels
 * too. A card that lands on the purse hides how many coins a kid has, which is
 * the one number they are working toward.
 *
 * A card pushed off its monster keeps pointing at it: the leader line stretches
 * and rotates, so the connection survives the clamp instead of breaking like
 * the old stems that terminated in mid-air.
 */
const HUD_SAFE = { left: 178, right: 190, top: 6, bottom: 132 }

/**
 * EVERY CARD IS THE SAME SHAPE.
 *
 * Cards used to be built by appending one slot per thing in the order, so three
 * side by side came out at wildly different widths and stopped reading as a set.
 * A card is always the same three-slot grid:
 *
 *      [ what it's in ] [ how many ] [ what's on top ]
 *
 * A slot with nothing in it draws an empty dish rather than collapsing, so a
 * card never changes shape. Mixed flavours stay inside the middle slot as
 * colour-coded counting dots instead of spawning extra slots.
 */
function slot(kind: 'base' | 'scoops' | 'topping'): HTMLElement {
  const s = el('div', 'mt-ticket-item')
  s.dataset['slot'] = kind
  return s
}

/** A hollow dish outline: "nothing goes here", drawn not written. */
function emptySlot(kind: 'base' | 'scoops' | 'topping'): HTMLElement {
  const s = slot(kind)
  s.dataset['empty'] = 'true'
  s.append(el('span', 'mt-slot-empty'))
  return s
}

/** Counting dots in each portion's OWN colour — the one-to-one correspondence. */
function scoopDots(scoops: readonly TicketPic[]): HTMLElement {
  const row = el('span', 'mt-dots')
  for (const p of scoops.slice(0, 8)) {
    const d = el('span', 'mt-dot')
    d.style.background = css(p.color)
    row.append(d)
  }
  return row
}

/** The three-slot grid, shared by an order card and the carry chip. */
function itemsGrid(pics: TicketPics, size: number): HTMLElement {
  const items = el('div', 'mt-ticket-items')

  if (pics.base === null) items.append(emptySlot('base'))
  else {
    const b = slot('base')
    b.append(itemPicture(pics.base, size))
    items.append(b)
  }

  if (pics.scoops.length === 0) items.append(emptySlot('scoops'))
  else {
    const s = slot('scoops')
    // The first portion is the picture; the dots carry the count AND the rest of
    // the colours, so a mixed order never grows the card.
    const first = pics.scoops[0]
    if (first !== undefined) s.append(itemPicture(first, size))
    s.append(scoopDots(pics.scoops))
    items.append(s)
  }

  if (pics.topping === null) items.append(emptySlot('topping'))
  else {
    const t = slot('topping')
    t.append(itemPicture(pics.topping, size))
    items.append(t)
  }
  return items
}

export function installTickets(hud: HTMLElement): TicketApi {
  const layer = el('div', 'mt-tickets')
  hud.append(layer)

  const tray = el('div', 'mt-ticket')
  tray.classList.add('mt-tray')
  tray.style.display = 'none'
  hud.append(tray)

  const tickets: Ticket[] = []

  const syncRailFlag = (): void => {
    hud.dataset['tickets'] = tickets.some((t) => t.placed) ? '1' : '0'
  }

  const ticketNode = (pics: TicketPics, look: MonsterConfig): { node: HTMLElement; tail: HTMLElement } => {
    const node = el('div', 'mt-ticket')
    node.dataset['done'] = 'false'
    const colour = css(look.color)
    // The card wears the monster's own colour on its band, its border and its
    // tail, so even mid-move there is never a moment where you cannot tell whose
    // it is.
    node.style.borderColor = css('ink')
    node.style.setProperty('--who', colour)

    const band = el('div', 'mt-ticket-band')
    band.style.background = colour

    /*
     * NO PORTRAIT ON AN ORDER CARD (venue fix, r3). The card hangs off the head
     * of the monster that asked and wears its body colour on the band, the
     * border and the tail — the face inside it was a third copy of an answer a
     * kid already has, and it was costing 40% of a card that was covering the
     * room. What is left is the ORDER: three pictures, nothing else. (The carry
     * chip keeps its face: that one is answering a different question — "this
     * is MINE" — and it is not sitting on top of the world.)
     */
    const items = itemsGrid(pics, 30)

    const tail = el('div', 'mt-ticket-tail')
    tail.style.setProperty('--who', colour)

    node.append(band, items, tail)
    return { node, tail }
  }

  const add = (key: string, pics: TicketPics, look: MonsterConfig): void => {
    while (tickets.length >= MAX_TICKETS) {
      tickets.shift()?.node.remove()
    }
    const { node, tail } = ticketNode(pics, look)
    // Born hidden. A card only becomes visible once the venue crew has told us
    // where its monster is — that is what makes a stale card structurally
    // impossible (see `venue:anchors`).
    node.style.display = 'none'
    layer.append(node)
    tickets.push({ key, node, tail, color: css(look.color), placed: false })
    syncRailFlag()
  }

  /**
   * Retire the card for the customer who was actually served. Falls back to the
   * oldest only when the venue could not say who — which is what it always used
   * to do, and it took the wrong card off whenever a kid served the second
   * monster in the queue before the first.
   */
  const markDone = (key: string | null): void => {
    const i = key === null ? 0 : tickets.findIndex((t) => t.key === key)
    if (i < 0) return
    const t = tickets.splice(i, 1)[0]
    if (t === undefined) return
    t.node.dataset['done'] = 'true'
    window.setTimeout(() => t.node.remove(), 700)
    syncRailFlag()
  }

  const clearAll = (): void => {
    for (const t of tickets) t.node.remove()
    tickets.length = 0
    tray.style.display = 'none'
    syncRailFlag()
  }

  /**
   * PUT EACH CARD OVER ITS MONSTER.
   *
   * The anchor is a canvas-pixel point just above the customer's head, computed
   * by the venue crew from the CANVAS bounding rect (never window metrics — on
   * an older iPad the window height does not match the canvas and every card
   * would sit one slot off, which is a bug that has already shipped once in this
   * codebase).
   *
   * A card whose monster is not in this frame's list is HIDDEN, not left
   * hovering. That is the whole fix for order tickets appearing in the town
   * shop: with no venue there are no anchors, and with no anchors there are no
   * cards, whatever route the kid took out of the room.
   */
  const placeCards = (anchors: readonly { key: string; x: number; y: number; color: string }[]): void => {
    const byKey = new Map(anchors.map((a) => [a.key, a]))
    const w = hud.clientWidth
    const h = hud.clientHeight
    const minL = HUD_SAFE.left
    const maxL = Math.max(minL, w - HUD_SAFE.right - CARD_W)
    const minT = HUD_SAFE.top
    const maxT = Math.max(minT, h - HUD_SAFE.bottom - CARD_H)

    /*
     * A CARD ONLY EXISTS WHILE YOU CAN SEE WHOSE IT IS (venue fix, r3).
     *
     * The clamp used to drag any anchor into the safe box however far off the
     * picture its monster was, so a customer scrolled off the top-left of the
     * frame left a card jammed under the ceiling with a long leader line running
     * away behind the button stack — "an order with no visible monster attached
     * to it", which is the one thing this whole system exists to prevent. If the
     * head is not in the frame the card waits, exactly as its customer does.
     */
    const wanted: { t: Ticket; a: { x: number; y: number }; left: number; top: number }[] = []
    for (const t of tickets) {
      const a = byKey.get(t.key)
      const onScreen = a !== undefined && a.x > -24 && a.y > -24 && a.x < w + 24 && a.y < h + 24
      if (a === undefined || !onScreen) {
        if (t.placed) {
          t.placed = false
          t.node.style.display = 'none'
          syncRailFlag()
        }
        continue
      }
      wanted.push({
        t,
        a,
        left: Math.min(Math.max(a.x - CARD_W / 2, minL), maxL),
        top: Math.min(Math.max(a.y - CARD_H - TAIL_H, minT), maxT),
      })
    }

    /*
     * ...AND NO TWO CARDS SHARE A PATCH OF SCREEN. Three customers sitting near
     * each other put three cards on top of one another, which is unreadable at
     * any age. Cards are pushed apart along whichever axis needs less movement,
     * then re-clamped, so each one stays as close to its own monster as the room
     * allows. A handful of comparisons on at most four cards.
     */
    const GAP = 8
    for (let pass = 0; pass < SEPARATE_PASSES; pass++) {
      let moved = false
      for (let i = 0; i < wanted.length; i++) {
        for (let j = i + 1; j < wanted.length; j++) {
          const p = wanted[i]
          const q = wanted[j]
          if (p === undefined || q === undefined) continue
          const ox = CARD_W + GAP - Math.abs(p.left - q.left)
          const oy = CARD_H + GAP - Math.abs(p.top - q.top)
          if (ox <= 0 || oy <= 0) continue
          moved = true
          if (ox < oy) {
            const s = p.left <= q.left ? 1 : -1
            p.left -= (s * ox) / 2
            q.left += (s * ox) / 2
          } else {
            const s = p.top <= q.top ? 1 : -1
            p.top -= (s * oy) / 2
            q.top += (s * oy) / 2
          }
        }
      }
      if (!moved) break
    }

    for (const it of wanted) {
      const left = Math.round(Math.min(Math.max(it.left, minL), maxL))
      const top = Math.round(Math.min(Math.max(it.top, minT), maxT))
      const t = it.t
      t.node.style.display = 'block'
      t.node.style.transform = `translate3d(${left}px, ${top}px, 0)`

      // THE LEADER LINE. It leaves the card at whichever point on the bottom
      // edge is nearest the monster and ends in a dot ON the monster's head, so
      // there is never a stem pointing at nothing.
      const originX = Math.min(Math.max(it.a.x, left + 18), left + CARD_W - 18)
      const originY = top + CARD_H
      const dx = it.a.x - originX
      const dy = it.a.y - originY
      const len = Math.max(8, Math.hypot(dx, dy))
      t.tail.style.left = `${Math.round(originX - left)}px`
      t.tail.style.width = `${Math.round(len)}px`
      t.tail.style.transform = `rotate(${Math.atan2(dy, dx).toFixed(4)}rad)`
      if (!t.placed) {
        t.placed = true
        syncRailFlag()
      }
    }
  }

  // -- venue crew events (merged types; no cross-crew import) ---------------
  bus.on('venue:ordered', (e) => {
    add(e.key, e.pics, e.look)
  })

  bus.on('venue:anchors', (e) => placeCards(e.anchors))

  bus.on('venue:served', (e) => {
    // A wrong order is a giggle and the card STAYS UP — the customer is still
    // sitting there wanting the same thing (LAW #2).
    if (e.correct) markDone(e.key)
  })

  /**
   * WHAT I AM HOLDING — a different question from "what was ordered", and it
   * looks like a different question: same three-slot grid so a kid can compare
   * it to a card slot by slot, but it wears the player's own face, a carry
   * marker and its own colour, so it reads as MINE rather than as another order.
   */
  const renderTray = (pics: TicketPics): void => {
    const empty = pics.base === null && pics.scoops.length === 0 && pics.topping === null
    if (empty) {
      tray.style.display = 'none'
      return
    }
    tray.replaceChildren()
    const face = el('div', 'mt-ticket-face')
    face.append(monsterPortrait(app.monster, 46))
    const carry = el('span', 'mt-tray-mark')
    carry.append(iconEl('plate', 30))
    face.append(carry)
    tray.append(face, itemsGrid(pics, 46))
    tray.style.display = 'flex'
  }

  bus.on('venue:trayChanged', (e) => renderTray(e.pics))

  /**
   * "THEY TOLD A FRIEND." The brief's growth loop in one event: happy customers
   * tip, tell friends, and more monsters show up, which is the pressure to build
   * more tables. Nothing listened, so the REASON the shop got busier was
   * invisible — another monster simply appeared.
   */
  bus.on('venue:friendComing', () => {
    bus.emit('ui:toast', { icon: 'monster', ms: 2600 })
  })

  // Leaving the venue clears everything: cards belong to a place. `venue:closed`
  // is the belt to `scene:left`'s braces — it fires off an active-scene watch,
  // so it also catches scene changes that emit no scene events at all.
  bus.on('venue:closed', clearAll)
  bus.on('scene:left', clearAll)
  bus.on('save:loaded', clearAll)

  return {
    demo: () => {
      clearAll()
      const pic = (shape: string, color: PaletteName): TicketPic => ({ shape, color })
      const demos: { key: string; pics: TicketPics; look: MonsterConfig; at: [number, number] }[] = [
        {
          key: 'demo1',
          pics: {
            base: pic('cone', 'cone'),
            scoops: [pic('ball', 'iceStrawberry'), pic('ball', 'iceStrawberry')],
            topping: pic('cherry', 'cherry'),
          },
          look: { ...DEFAULT_MONSTER, color: 'monsterCoral', horns: 'curved', pattern: 'spots' },
          at: [0.3, 0.55],
        },
        {
          key: 'demo2',
          pics: { base: pic('cup', 'plate'), scoops: [pic('ball', 'iceMint')], topping: null },
          look: { ...DEFAULT_MONSTER, color: 'monsterLilac', eyes: 'three', horns: 'antennae', pattern: 'stripes' },
          at: [0.5, 0.62],
        },
        {
          key: 'demo3',
          pics: {
            base: pic('plate', 'plate'),
            scoops: [pic('ball', 'roofMustard'), pic('ball', 'leafLight')],
            topping: pic('sprinkles', 'flowerYellow'),
          },
          look: { ...DEFAULT_MONSTER, color: 'monsterSunny', eyes: 'sleepy', horns: 'antlers', pattern: 'belly' },
          at: [0.72, 0.5],
        },
      ]
      for (const d of demos) add(d.key, d.pics, d.look)
      placeCards(
        demos.map((d) => ({
          key: d.key,
          x: hud.clientWidth * d.at[0],
          y: hud.clientHeight * d.at[1],
          color: css(d.look.color),
        })),
      )
      // Paint the tray DIRECTLY. A UI test hook must never emit another crew's
      // event: `venue:trayChanged` is the venue crew's state, and faking it from
      // here would drive their simulation from a screenshot tool.
      renderTray({ base: pic('cone', 'cone'), scoops: [pic('ball', 'iceStrawberry')], topping: null })
    },
    clear: clearAll,
    count: () => tickets.length,
  }
}
